import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { Input } from './ui/input';
import OnOffToggle from './OnOffToggle';
import { Star, Loader2, Link2, Mail, Ticket, Copy, Check, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import apiV2 from '../utils/apiV2';
import TabPageLayout from './TabPageLayout';
import FormSection from './FormSection';

// SWR fetcher
const fetcher = async (url) => {
  const response = await apiV2.get(url);
  return response.data;
};

/**
 * ReviewsTab - Step-based UI
 * 
 * Step 1: Enable Reviews (blue)
 * Step 2: Review Link (purple) - defaults to BookingFlows
 * Step 3: Distribution (green)
 */
const ReviewsTab = () => {
  const [saving, setSaving] = useState(false);
  const [savingField, setSavingField] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [settings, setSettings] = useState({
    reviewsEnabled: false,
    reviewLinkUrl: '',
    includeInThankYou: true,
    includeOnTicket: true,
  });

  const orgSlug = localStorage.getItem('organizationSlug');
  // Use window.location.origin for frontend URL (not the backend API URL)
  const frontendUrl = window.location.origin;
  const defaultReviewUrl = `${frontendUrl}/${orgSlug || 'your-business'}/review`;

  // Fetch settings
  const { data: orgData, isLoading, mutate } = useSWR(
    '/auth/organizations/me',
    fetcher,
    { revalidateOnFocus: true, dedupingInterval: 30000 }
  );

  // Load settings
  useEffect(() => {
    if (orgData) {
      setSettings({
        reviewsEnabled: orgData.reviewLinkEnabled || false,
        reviewLinkUrl: orgData.reviewLinkUrl || '',
        includeInThankYou: orgData.thankYouEmailIncludeReview !== false,
        includeOnTicket: orgData.reviewOnTicketEnabled !== false,
      });
    }
  }, [orgData]);

  // Auto-save function
  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      await apiV2.patch('/auth/organizations/me', {
        reviewLinkEnabled: newSettings.reviewsEnabled,
        reviewLinkUrl: newSettings.reviewLinkUrl || defaultReviewUrl,
        thankYouEmailIncludeReview: newSettings.includeInThankYou,
        reviewOnTicketEnabled: newSettings.includeOnTicket,
      });
      mutate();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
      setSavingField(null);
    }
  };

  // Handle toggle changes with auto-save
  const handleToggle = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    setSavingField(field);
    saveSettings(newSettings);
  };

  // Handle URL change with debounced save
  const handleUrlChange = (value) => {
    setSettings(prev => ({ ...prev, reviewLinkUrl: value }));
  };

  const handleUrlBlur = () => {
    setSavingField('reviewLinkUrl');
    saveSettings(settings);
  };

  // Reset to default BookingFlows URL
  const resetToDefault = () => {
    const newSettings = { ...settings, reviewLinkUrl: defaultReviewUrl };
    setSettings(newSettings);
    setSavingField('reviewLinkUrl');
    saveSettings(newSettings);
    toast.success('Reset to BookingFlows review link');
  };

  // Copy review link to clipboard
  const copyReviewLink = async () => {
    const linkToCopy = settings.reviewLinkUrl || defaultReviewUrl;
    try {
      await navigator.clipboard.writeText(linkToCopy);
      setCopied(true);
      toast.success('Review link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Check if using custom URL (not the default)
  const isUsingCustomUrl = settings.reviewLinkUrl && settings.reviewLinkUrl !== defaultReviewUrl;

  return (
    <TabPageLayout
      title="Reviews"
      subtitle="Collect and display customer reviews"
      loading={isLoading}
    >
      <div className="space-y-6">
        
        {/* Step 1: Enable Reviews - BLUE */}
        <FormSection
          stepNumber={1}
          title="Enable Reviews"
          description="Reviews display on your marketplace profile and help build trust with new customers"
          color="blue"
        >
          <div className="flex items-center justify-between p-4 bg-blue-50/50 rounded-lg border border-blue-100">
            <div>
              <p className="font-medium text-gray-900">Collect Customer Reviews</p>
              <p className="text-sm text-gray-500 mt-1">
                Customers with bookings in the last 90 days can leave a review
              </p>
            </div>
            <div className="flex items-center gap-3">
              {savingField === 'reviewsEnabled' && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
              <OnOffToggle
                value={settings.reviewsEnabled}
                onChange={(checked) => handleToggle('reviewsEnabled', checked)}
                data-testid="enable-reviews-toggle"
              />
            </div>
          </div>
        </FormSection>

        {/* Step 2: Review Link - PURPLE */}
        <FormSection
          stepNumber={2}
          title="Review Destination"
          description="Where should customers leave reviews?"
          color="purple"
        >
          <div className={`space-y-4 ${!settings.reviewsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={settings.reviewLinkUrl || defaultReviewUrl}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  onBlur={handleUrlBlur}
                  placeholder={defaultReviewUrl}
                  className="pl-10"
                  disabled={!settings.reviewsEnabled}
                  data-testid="review-link-input"
                />
              </div>
              <button
                onClick={copyReviewLink}
                disabled={!settings.reviewsEnabled}
                className="px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="copy-review-link-btn"
                title="Copy link"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Paste a Google or TripAdvisor link to redirect customers there instead
              </p>
              {isUsingCustomUrl && (
                <button
                  onClick={resetToDefault}
                  disabled={!settings.reviewsEnabled}
                  className="flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors"
                  data-testid="reset-to-default-btn"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset to BookingFlows
                </button>
              )}
            </div>
          </div>
        </FormSection>

        {/* Step 3: Distribution - GREEN */}
        <FormSection
          stepNumber={3}
          title="Ask for Reviews"
          description="Where should we prompt customers for reviews?"
          color="green"
        >
          <div className={`space-y-2 ${!settings.reviewsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            
            {/* Include on Ticket */}
            <div className="flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-4">
                <div className="p-2.5 rounded-xl bg-green-100">
                  <Ticket className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <span className="font-medium text-gray-900">Customer Ticket</span>
                  <p className="text-sm text-gray-500 mt-0.5">Review button on booking confirmation</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {savingField === 'includeOnTicket' && <Loader2 className="w-4 h-4 animate-spin text-green-500" />}
                <OnOffToggle
                  value={settings.includeOnTicket}
                  onChange={(checked) => handleToggle('includeOnTicket', checked)}
                  disabled={!settings.reviewsEnabled}
                  data-testid="include-on-ticket-toggle"
                />
              </div>
            </div>
            
            {/* Include in Thank You Email */}
            <div className="flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-4">
                <div className="p-2.5 rounded-xl bg-green-100">
                  <Mail className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <span className="font-medium text-gray-900">Thank You Email</span>
                  <p className="text-sm text-gray-500 mt-0.5">Sent 24 hours after their experience</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {savingField === 'includeInThankYou' && <Loader2 className="w-4 h-4 animate-spin text-green-500" />}
                <OnOffToggle
                  value={settings.includeInThankYou}
                  onChange={(checked) => handleToggle('includeInThankYou', checked)}
                  disabled={!settings.reviewsEnabled}
                  data-testid="include-in-email-toggle"
                />
              </div>
            </div>
          </div>
        </FormSection>

      </div>
    </TabPageLayout>
  );
};

export default ReviewsTab;
